/**
 * Live feedback, computed from the clues alone (CLAUDE.md §10): the picture never reaches
 * this code. A line is flagged only when it can no longer satisfy its clue.
 */
import type { Feedback } from "../types";
import { columns, lineContradiction, runs } from "./clues";
import { EMPTY } from "./types";
import { cellId, type Axis, type PicrossDefinition, type PicrossProgress } from "./types";
import { resizeRows } from "./progress";

export const LINE_CONTRADICTION = "line_contradiction";

export function lineKey(axis: Axis, index: number): string {
  return `${axis}:${index}`;
}

export function contradictions(
  definition: PicrossDefinition,
  rows: string[],
): { line: Axis; index: number }[] {
  const grid = resizeRows(rows, definition.rows, definition.cols);
  const found: { line: Axis; index: number }[] = [];
  grid.forEach((line, index) => {
    if (lineContradiction(line, definition.clues.rows[index] ?? []))
      found.push({ line: "row", index });
  });
  columns(grid).forEach((line, index) => {
    if (lineContradiction(line, definition.clues.cols[index] ?? []))
      found.push({ line: "col", index });
  });
  return found;
}

/** Lines fully decided whose runs are exactly the clue: the player is done with them. */
export function doneLines(
  definition: PicrossDefinition,
  rows: string[],
): { line: Axis; index: number }[] {
  const grid = resizeRows(rows, definition.rows, definition.cols);
  const satisfied = (line: string, clue: number[]) =>
    !line.includes(EMPTY) && runs(line).join(",") === clue.join(",");
  const found: { line: Axis; index: number }[] = [];
  grid.forEach((line, index) => {
    if (satisfied(line, definition.clues.rows[index] ?? [])) found.push({ line: "row", index });
  });
  columns(grid).forEach((line, index) => {
    if (satisfied(line, definition.clues.cols[index] ?? [])) found.push({ line: "col", index });
  });
  return found;
}

export function localFeedback(
  definition: PicrossDefinition,
  progress: PicrossProgress,
): Feedback[] {
  return contradictions(definition, progress.rows).map(({ line, index }) => ({
    type: line,
    code: LINE_CONTRADICTION,
    index,
    cells:
      line === "row"
        ? Array.from({ length: definition.cols }, (_, c) => cellId(index, c))
        : Array.from({ length: definition.rows }, (_, r) => cellId(r, index)),
  }));
}
